import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useQuery } from "@apollo/react-hooks";
import { useDispatch } from "@dhn/components/contexts/app-context";
import { useAuth0 } from "@dhn/components/contexts/react-auth0-spa";
import { Button } from "@dhn/components/ui/button";
import { cn } from "@dhn/lib/utils";
import CompanyLogo from "@dhn/components/CompanyLogo";
import DynamicBreadcrumb from "@dhn/components/dynamic-bread-crumb/DynamicBreadcrumb";
import {
  GET_COMPANY_INFO,
  getCompanyDisplayName,
} from "@dhn/shared/company-info";
import { ACCOUNT_CONFIG_ITEMS } from "@dhn/shared/access-provider";
import noLogo from "../assets/img/brand/noLogo";
import NavMenu from "./DoctorsOfficeLayout/components/NavMenu";
import NavbarRight from "./DoctorsOfficeLayout/components/NavbarRight";
import AccessUserProfileModal from "./access-provider/AccessUserProfileModal";
// Mismo diseño que el navbar del consultorio.
import "./DoctorsOfficeLayout/style.css";
import "./DoctorsOfficeLayout/navbar-refresh.css";

export default function AccessProviderNavbar({
  profile,
  user,
  pharmacy,
  companyInfo: companyInfoProp,
  isCompanyInfoLoading = false,
  menuConfig,
  sortMenuAlphabetically = true,
  providerLabel = "Sucursal",
  providerLogoAlt = "logo farmacia",
}) {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const auth = useAuth0();
  const { getTokenSilently } = auth;
  const [isOpen, setIsOpen] = useState(false);
  const [logo, setLogo] = useState(noLogo);

  const { data: { legalEntity: queriedCompanyInfo } = {}, loading } = useQuery(
    GET_COMPANY_INFO,
    {
      skip: !!companyInfoProp,
      fetchPolicy: "cache-and-network",
    },
  );

  const companyInfo = companyInfoProp || queriedCompanyInfo;
  const companyName = getCompanyDisplayName(companyInfo);
  const loadingCompanyInfo = (isCompanyInfoLoading || loading) && !companyInfo;
  const permissionViews = profile?.views;
  const branchName = user?.branchOffice?.name || providerLabel;
  const breadcrumbTitles = useMemo(
    () =>
      Object.values(menuConfig).reduce(
        (titles, item) => ({ ...titles, [item.path]: item.label }),
        {},
      ),
    [menuConfig],
  );

  useEffect(() => {
    let isMounted = true;
    let objectUrl = "";
    const pharmacyLogo = Array.isArray(pharmacy)
      ? pharmacy[pharmacy.length - 1]
      : null;

    if (!pharmacyLogo?.name) {
      setLogo(noLogo);
      return undefined;
    }

    async function getLogoPharmacy() {
      try {
        const accessToken = await getTokenSilently();
        const uri = `${process.env.REACT_APP_APOLLO_CLIENT_URI}/claim-files/${pharmacyLogo.name}`;

        const response = await fetch(uri, {
          body: JSON.stringify(pharmacyLogo),
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.status >= 200 && response.status <= 201) {
          const arraybuffer = await response.arrayBuffer();
          const blob = new Blob([arraybuffer], { type: "image/png" });
          objectUrl = URL.createObjectURL(blob);

          if (isMounted) {
            setLogo(objectUrl);
          }
          return;
        }
      } catch (_) {}

      if (isMounted) {
        setLogo(noLogo);
      }
    }

    getLogoPharmacy();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [pharmacy, getTokenSilently]);

  const toggle = () => setIsOpen((value) => !value);

  const signOut = (event) => {
    event?.preventDefault?.();
    dispatch({ type: "LOGOUT" });
    auth.logout({ returnTo: window.location.origin });
  };

  return (
    <div className="doctor-office-shell">
      <header className="doctor-office-navbar tw-sticky tw-top-0 tw-z-40 tw-border-b tw-border-clinic-line/80 tw-bg-white/95 tw-shadow-clinic-soft tw-backdrop-blur">
        <nav className="doctor-office-navbar__inner tw-flex tw-min-h-[88px] tw-w-full tw-flex-wrap tw-items-center tw-gap-3 tw-px-4 tw-py-3 xl:tw-flex-nowrap xl:tw-px-6">
          <NavLink
            to="/"
            className="doctor-office-navbar__brand tw-flex tw-items-center tw-rounded-md tw-pr-2 tw-no-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring"
          >
            <CompanyLogo
              companyInfo={companyInfo}
              loading={loadingCompanyInfo}
              alt={companyName}
              className="doctor-office-navbar__logo tw-h-16 tw-w-auto tw-object-contain sm:tw-h-20 xl:tw-h-24"
              loaderClassName="doctor-office-navbar__logo tw-h-16 tw-w-28 sm:tw-h-20 xl:tw-h-24"
              loaderStyle={{ fontSize: 20, color: "#1f7db7" }}
            />
          </NavLink>

          <Button
            type="button"
            variant="outline"
            size="icon"
            className="doctor-office-navbar__toggle tw-ml-auto tw-border-clinic-line tw-text-clinic-ink xl:tw-hidden"
            onClick={toggle}
            aria-expanded={isOpen}
            aria-label="Alternar navegación"
            title="Alternar navegación"
          >
            {isOpen ? (
              <X className="tw-h-5 tw-w-5" />
            ) : (
              <Menu className="tw-h-5 tw-w-5" />
            )}
          </Button>

          <div
            className={cn(
              "doctor-office-navbar__content tw-w-full tw-min-w-0 tw-items-center tw-gap-4 xl:tw-flex xl:tw-w-auto xl:tw-flex-1",
              isOpen ? "tw-block" : "tw-hidden xl:tw-flex",
              isOpen && "doctor-office-navbar__content--open",
            )}
          >
            <NavMenu
              permissionViews={permissionViews}
              pathname={pathname}
              menuConfig={menuConfig}
              sortAlphabetically={sortMenuAlphabetically}
            />

            <NavbarRight
              user={user}
              profileName={profile?.name}
              permissionViews={permissionViews}
              logo={logo}
              logoAlt={providerLogoAlt}
              centerName={branchName}
              userCardLabel="Ver información del usuario"
              configItems={ACCOUNT_CONFIG_ITEMS}
              configSubtitle="Opciones de la cuenta"
              onSignOut={signOut}
              renderProfileModal={({ isOpen: isProfileOpen, onClose }) => (
                <AccessUserProfileModal
                  isOpen={isProfileOpen}
                  onClose={onClose}
                  user={user}
                  profile={profile}
                  providerLabel={providerLabel}
                />
              )}
            />
          </div>
        </nav>
      </header>

      {pathname !== "/" && <DynamicBreadcrumb titles={breadcrumbTitles} />}
    </div>
  );
}
